import React from 'react';
import type { RequirementWithDetails } from '../services/requirementService';
import type { ProjectMember } from '../services/projectService';

interface TeamWorkloadCardProps {
  requirements: RequirementWithDetails[];
  members: ProjectMember[];
}

interface MemberWorkload {
  userId: string;
  name: string;
  email: string;
  totalAssigned: number;
  activeAssigned: number; // in-progress + approved
  completedAssigned: number;
}

export function TeamWorkloadCard({ requirements, members }: TeamWorkloadCardProps): React.ReactElement {
  // Calculate workload for each member
  const calculateWorkload = (): MemberWorkload[] => {
    const workloadMap = new Map<string, MemberWorkload>();

    // Initialize all members with zero counts
    members.forEach(member => {
      if (member.user_id) {
        workloadMap.set(member.user_id, {
          userId: member.user_id,
          name: `${member.user_first_name || ''} ${member.user_last_name || ''}`.trim(),
          email: member.user_email || '',
          totalAssigned: 0,
          activeAssigned: 0,
          completedAssigned: 0
        });
      }
    });

    // Count requirements for each assigned member
    requirements.forEach(req => {
      if (req.assigned_to && req.is_active) {
        const workload = workloadMap.get(req.assigned_to);
        if (workload) {
          workload.totalAssigned++;

          if (req.status === 'completed') {
            workload.completedAssigned++;
          } else if (req.status === 'in-progress' || req.status === 'approved') {
            workload.activeAssigned++;
          }
        }
      }
    });

    // Convert to array and sort by total assigned (descending)
    return Array.from(workloadMap.values()).sort((a, b) => b.totalAssigned - a.totalAssigned);
  };

  const workloads = calculateWorkload();

  // Get workload color based on active count
  const getWorkloadColor = (activeCount: number): string => {
    if (activeCount === 0) return 'text-gray-600 bg-gray-100 border-gray-300';
    if (activeCount < 5) return 'text-green-700 bg-green-50 border-green-300';
    if (activeCount <= 10) return 'text-yellow-700 bg-yellow-50 border-yellow-300';
    return 'text-red-700 bg-red-50 border-red-300';
  };

  // Get workload bar color
  const getWorkloadBarColor = (activeCount: number): string => {
    if (activeCount === 0) return 'bg-gray-400';
    if (activeCount < 5) return 'bg-green-500';
    if (activeCount <= 10) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  // Get initials
  const getInitials = (name: string): string => {
    const parts = name.trim().split(' ');
    if (parts.length === 0 || !parts[0]) return '?';
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  // Calculate max workload for bar width scaling
  const maxWorkload = Math.max(...workloads.map(w => w.activeAssigned), 1);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-50 mb-4">Team Workload Distribution</h3>

      {workloads.length === 0 ? (
        <div className="text-center py-8">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
            />
          </svg>
          <p className="mt-2 text-sm text-gray-500">No team members yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {workloads.map(workload => (
            <div key={workload.userId} className="border border-gray-200 rounded-lg p-4">
              {/* Member Header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  {/* Avatar */}
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-r from-indigo-100 to-blue-100 flex items-center justify-center border border-indigo-200">
                    <span className="text-sm font-semibold text-indigo-700 dark:text-indigo-300">
                      {getInitials(workload.name)}
                    </span>
                  </div>

                  {/* Name and Email */}
                  <div>
                    <p className="text-sm font-medium text-gray-900">{workload.name}</p>
                    <p className="text-xs text-gray-500">{workload.email}</p>
                  </div>
                </div>

                {/* Workload Badge */}
                <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium border ${getWorkloadColor(workload.activeAssigned)}`}>
                  <span className="font-semibold">{workload.activeAssigned}</span>
                  <span className="text-xs">active</span>
                </div>
              </div>

              {/* Workload Bar */}
              {workload.totalAssigned > 0 ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs text-gray-600">
                    <span>Active: {workload.activeAssigned}</span>
                    <span>Completed: {workload.completedAssigned}</span>
                    <span>Total: {workload.totalAssigned}</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-300 ${getWorkloadBarColor(workload.activeAssigned)}`}
                      style={{ width: `${(workload.activeAssigned / maxWorkload) * 100}%` }}
                    />
                  </div>
                </div>
              ) : (
                <p className="text-xs text-gray-500 text-center">No assignments</p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Workload Legend */}
      {workloads.length > 0 && (
        <div className="mt-6 pt-4 border-t border-gray-200">
          <p className="text-xs font-medium text-gray-700 mb-2">Workload Indicators:</p>
          <div className="flex flex-wrap gap-3 text-xs">
            <div className="inline-flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500" />
              <span className="text-gray-600">Light (&lt;5 active)</span>
            </div>
            <div className="inline-flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-yellow-500" />
              <span className="text-gray-600">Moderate (5-10 active)</span>
            </div>
            <div className="inline-flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500" />
              <span className="text-gray-600">Heavy (&gt;10 active)</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
